import React, { useState, useEffect } from 'react'
import { styled } from '@mui/material'
import Stack from '@mui/material/Stack'

//components
import StyledTable from '../../components/ReactTable/StyledTable'
import StyledTableContainer from '../../components/StyledTableContainer/StyledTableContainer'
import StyledEmptyHeader from '../../components/StyledEmptyHeader/StyledEmptyHeader'
import StyledSelectField from '../../components/StyledSelectField/StyledSelectField'
import RadialGauge from '../../components/RadialGauge/RadialGauge'
import IdletimeWrapperMachineWise from './IdletimeWrapperMachineWise'
import RejectionWrapperMachineWiseUnit4 from './RejectionWrapperMachineWiseUnit4'
import RejectionWrapperMachineWise from './RejectionWrapperMachineWise'
import { useDateSelectorContext } from '../../context/DateSelectorContext'
import { useDashboardGlobalStateContext } from '../../context/DashboardGlobalStateContext'
import { normalizeOperationFields } from '../../utils/operation.utils'

//utils
import { showNotification } from '../../utils/toast.utils'
import * as dashboardService from '../../service/dashboard.service'

const OEEWrapper = styled('div')(() => ({
  width: '100%',
  display: 'flex',
  gap: '24px',
  flex: 1
}))

const KPITable = styled('table')(() => ({
  borderCollapse: 'collapse',
  width: '100%'
}))

const KPITableRow = styled('tr')(() => ({
  width: '100%'
}))

const KPITableEntry = styled('td')(() => ({
  border: '1px solid #dddddd',
  textAlign: 'left',
  padding: '8px'
}))

const OEECard = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  justifyContent: 'center',
  alignItems: 'center',
  flex: 1,
  padding: '16px',
  border: '1px solid #ddd',
  borderRadius: '12px'
}))

const KPIItem = styled('div')(() => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  flex: 1
}))

let defaultMachine = {
  value: 'overall',
  label: 'All machines'
}

const MachineWise = () => {
  const defaultPartFamily = {
    value: 'all',
    label: 'All part operations'
  }
  //context
  const { selectedDate } = useDateSelectorContext()
  const { machines: machineOptions } = useDashboardGlobalStateContext()

  //local
  const [availableShifts, setAvailableShifts] = useState([])
  const [machineWiseData, setMachineWiseData] = useState({})
  const [machine, setMachine] = useState(defaultMachine)
  const [selectedPartFamily, setSelectedPartFamily] =
    useState(defaultPartFamily)
  const [selectedShift, setSelectedShift] = useState({
    label: 'All shifts',
    value: 'day'
  })
  const [isLoading, setIsLoading] = useState(false)

  const [availablePartFamilyShiftWise, setAvailablePartFamilyShiftWise] =
    useState({})

  useEffect(() => {
    if (availablePartFamilyShiftWise[selectedShift.value]) {
      setSelectedPartFamily({
        label: availablePartFamilyShiftWise[selectedShift.value][0],
        value: availablePartFamilyShiftWise[selectedShift.value][0]
      })
    }
  }, [selectedShift, availablePartFamilyShiftWise])

  const fetchData = async () => {
    const params = {}

    if (selectedDate) {
      params.date = selectedDate
    }

    if (machine.value !== 'overall') {
      params.machine = machine.value
    }

    try {
      setIsLoading(true)

      const listOfPromises = [
        dashboardService.getDailyReportMachineWiseV2(params)
      ]

      const response = await Promise.allSettled(listOfPromises)

      if (response[0].status === 'fulfilled') {
        const rawData = response[0].value.data.data.productionInfo

        const liveData = {
          ...rawData,
          variantsArray: normalizeOperationFields(rawData.variantsArray || [])
        }
        const availableShifts = response[0].value.data.data.availableShifts
        const partFamilyOptions = {
          S1: [],
          S2: [],
          S3: [],
          day: []
        }

        const filteredLiveData = {
          ...liveData,
          variantsArray: liveData.variantsArray.filter(
            (item) => item.partCount !== 0
          )
        }

        setAvailablePartFamilyShiftWise(partFamilyOptions)
        setSelectedPartFamily({
          label: partFamilyOptions.day[0],
          value: partFamilyOptions.day[0]
        })
        setMachineWiseData(filteredLiveData)
        setAvailableShifts([
          {
            label: 'All shifts',
            value: 'day'
          },
          ...availableShifts.map((shift) => {
            return { label: shift, value: shift }
          })
        ])
      } else {
        showNotification(response[0].reason.message, 'error')
      }
    } catch (err) {
      console.log(err)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [selectedDate])

  const machinesWithAllOption = [
    { value: 'overall', label: 'All machines' },
    ...machineOptions
  ]

  let formattedData = []
  let OEEData = []
  if (machineWiseData) {
    if (machineWiseData.variantsArray) {
      formattedData = machineWiseData.variantsArray
    }

    if (machine && machineWiseData[machine.value]) {
      OEEData = Object.values(machineWiseData[machine.value])
    }
  }
  //1. Create search object
  const searchObj = {
    ...(machine &&
      machine.value &&
      machine.value !== 'overall' && {
        machine: machine.value
      }),
    ...(selectedShift &&
      selectedShift.value &&
      selectedShift.value !== 'day' && {
        shift: selectedShift.value
      })
  }
  //changed everything to 'day' for rendering upon page load
  //2. Extract the keys into an array
  const keysToCheck = Object.keys(searchObj)

  //3. filter
  if (keysToCheck.length > 0) {
    formattedData = formattedData.filter((productionData) => {
      return keysToCheck.every((key) => searchObj[key] === productionData[key])
    })
  }

  if (searchObj['shift']) {
    OEEData = OEEData.filter((oeeData) => {
      return searchObj['shift'] === oeeData['shift']
    })
  }

  const columns = [
    {
      Header: 'Machine',
      accessor: 'machine'
    },
    {
      Header: 'Shift',
      accessor: 'shift'
    },
    {
      Header: 'Part number',
      accessor: 'partName'
    },
    {
      Header: 'Operation',
      accessor: 'partType'
    },
    {
      Header: 'Work order',
      accessor: 'workOrderNum'
    },
    {
      Header: 'WO quantity',
      accessor: 'quantity'
    },
    {
      Header: 'Production ID',
      accessor: 'productionId'
    },
    {
      Header: 'Part count',
      accessor: 'partCount'
    },
    {
      Header: 'Target',
      accessor: 'target'
    },
    {
      Header: 'Rejection',
      accessor: 'rejectionCount'
    }
  ]

  const KPIList = (shift) => [
    {
      name: 'Availability',
      value: shift.availability * 100
    },
    {
      name: 'Quality',
      value: shift.quality * 100
    },
    {
      name: 'Performance',
      value: shift.performance * 100
    }
    // {
    //   name: 'OEE',
    //   value: shift.OEE
    // }
  ]

  const parameters = (shift) => [
    {
      name: 'Achieved quantity',
      value: shift.partCount
    },
    {
      name: 'Shift target',
      value: shift.shiftTarget
    },
    {
      name: 'Rejected quantity',
      value: shift.rejectionCount
    },
    {
      name: 'Total time',
      value: shift.totalTime
    },
    {
      name: 'Idletime',
      value: shift.idleTime
    }
  ]

  return (
    <>
      <Stack
        sx={{
          position: 'sticky',
          top: 0,
          width: '100%',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '24px',
          padding: '16px',
          backgroundColor: '#fff',
          borderBottom: '1px solid #eeeeee',
          zIndex: 20
        }}
      >
        <StyledSelectField
          label=""
          placeHolderText="Select shift"
          selectedValue={selectedShift}
          selectOptions={availableShifts}
          onChangeHandler={(value) => setSelectedShift(value)}
          isMulti={false}
          isSearchable={true}
          isDisabled={false}
          noOptionsMessage="No shift found"
        />
        <StyledSelectField
          label=""
          placeHolderText="Select machine"
          selectedValue={machine}
          selectOptions={machinesWithAllOption}
          onChangeHandler={(value) => setMachine(value)}
          isMulti={false}
          isSearchable={true}
          isDisabled={false}
          noOptionsMessage="No machine found"
        />
      </Stack>
      <Stack
        sx={{
          width: '100%',
          gap: '16px',
          padding: '16px'
        }}
      >
        <OEEWrapper>
          {machine.value !== 0 &&
            Array.isArray(OEEData) &&
            OEEData.length > 0 &&
            OEEData.map((shift) => (
              <OEECard key={`${shift.shift}-${selectedDate}`}>
                <KPIItem>
                  <div
                    style={{
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      flex: 1
                    }}
                  >
                    <h3>{shift.shift}</h3>
                    <RadialGauge value={shift.OEE} />
                  </div>
                </KPIItem>

                <KPIItem>
                  <KPITable>
                    {parameters(shift).map((item) => (
                      <KPITableRow key={item.name}>
                        <KPITableEntry>{item.name}</KPITableEntry>
                        <KPITableEntry>
                          {item.name === 'Achieved quantity'
                            ? Number(item.value || 0).toFixed(2)
                            : item.value}
                        </KPITableEntry>
                      </KPITableRow>
                    ))}
                  </KPITable>
                </KPIItem>

                <KPIItem>
                  {KPIList(shift).map((item) => (
                    <div
                      key={`${shift.shift}-${item.name}`}
                      style={{ display: 'flex', flexDirection: 'column' }}
                    >
                      <h4>
                        {item.name}{' '}
                        <span style={{ fontWeight: 'normal' }}>
                          ({Number(item.value).toFixed(3) + '%'})
                        </span>{' '}
                      </h4>
                      <div
                        style={{
                          backgroundColor: 'rgb(192, 192, 192)',
                          width: '100%',
                          height: '10px',
                          borderRadius: '5px'
                        }}
                      >
                        <div
                          style={{
                            width:
                              `${
                                item.value > 100
                                  ? 100
                                  : Math.round(Number(item.value)).toFixed(2)
                              }` + '%',
                            height: '100%',
                            borderRadius: '4px',
                            backgroundColor: '#62a5e8'
                          }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </KPIItem>
              </OEECard>
            ))}
        </OEEWrapper>

        <StyledTableContainer>
          {isLoading && <StyledEmptyHeader>Loading...</StyledEmptyHeader>}
          {!isLoading && formattedData.length === 0 && (
            <StyledEmptyHeader>
              There are no parts available, try selecting different date.
            </StyledEmptyHeader>
          )}
          {!isLoading && formattedData.length > 0 && (
            <StyledTable
              pagination={false}
              columns={columns}
              data={formattedData}
              hiddenColumns={[]}
            />
          )}
        </StyledTableContainer>
        <IdletimeWrapperMachineWise
          selectedDate={selectedDate}
          machine={machine}
          selectedShift={selectedShift}
        />
        {process.env.REACT_APP_UNIT === 'unit1' && (
          <RejectionWrapperMachineWise
            selectedDate={selectedDate}
            machine={machine}
            selectedShift={selectedShift}
          />
        )}
        {process.env.REACT_APP_UNIT === 'unit4' && (
          <RejectionWrapperMachineWiseUnit4
            selectedDate={selectedDate}
            machine={machine}
            selectedShift={selectedShift}
            selectionComponent={
              <StyledSelectField
                label=""
                placeHolderText="Select part operation"
                selectedValue={selectedPartFamily}
                selectOptions={
                  availablePartFamilyShiftWise[selectedShift.value]
                    ? availablePartFamilyShiftWise[selectedShift.value].map(
                        (item) => ({
                          label: item,
                          value: item
                        })
                      )
                    : []
                }
                onChangeHandler={(value) => setSelectedPartFamily(value)}
                isMulti={false}
                isSearchable={true}
                isDisabled={false}
                noOptionsMessage="No part operation found"
              />
            }
          />
        )}
      </Stack>
    </>
  )
}

export default MachineWise
